import { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Combine, Loader2, RefreshCw } from 'lucide-react';
import { api, ErrorApi } from '../api';
import Ayuda from '../ui/Ayuda';
import ModalConfirmar from '../ui/ModalConfirmar';
import SelectorUbicacion from './SelectorUbicacion';

/* =====================================================================
   UBICACIONES REPETIDAS · CATÁLOGO VIVO F3.7 (gobernanza)
   =====================================================================
   La otra mitad de «crear al vuelo». El «¿es esta?» del selector evita
   casi todos los duplicados; los que pasen —dos personas a la vez, o
   «crear de todos modos»— salen aquí agrupados por nombre bajo el mismo
   lugar, y se juntan con la fusión que ya existe (migración 067): lo que
   guarda la que se va pasa a la que se queda con un traspaso de verdad, y
   la que se va queda dada de baja con su código reservado para siempre.
   ===================================================================== */
export default function UbicacionesRepetidas() {
  const [grupos, setGrupos] = useState(null);
  const [error, setError] = useState(null);
  const [elegidas, setElegidas] = useState({});
  const [confirmar, setConfirmar] = useState(null);
  const [hecho, setHecho] = useState(null);
  // A mano: dos lugares con nombres distintos que son el mismo sitio.
  const [seVa, setSeVa] = useState(null);
  const [queda, setQueda] = useState(null);
  const [confirmarAMano, setConfirmarAMano] = useState(false);

  const cargar = useCallback(() => {
    api.get('/catalogo-vivo/ubicaciones/repetidas')
      .then((r) => { setError(null); setGrupos(r?.grupos || []); })
      .catch((e) => setError(e instanceof ErrorApi ? e.message
        : 'No hay conexión con el servidor. Vuelve a intentarlo.'));
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const clave = (g) => g.ubicaciones.map((u) => u.id).join('-');
  const seQueda = (g) => elegidas[clave(g)] ?? g.se_queda;

  async function juntar(g, motivo) {
    const destino = seQueda(g);
    const queda = g.ubicaciones.find((u) => u.id === destino);
    // Una a una: la fusión es por ubicación y cada una deja su asiento.
    for (const u of g.ubicaciones) {
      if (u.id === destino) continue;
      await api.post(`/sitios/${u.id}/fusionar`, { id_destino: destino, motivo });
    }
    setHecho(`Listo: todo quedó en «${queda.nombre}» (${queda.codigo}).`);
    cargar();
  }

  async function juntarAMano(motivo) {
    await api.post(`/sitios/${seVa.id}/fusionar`, { id_destino: queda.id, motivo });
    setHecho(`Listo: todo quedó en «${queda.nombre}» (${queda.codigo}).`);
    setSeVa(null);
    setQueda(null);
    cargar();
  }
  const mismo = seVa && queda && seVa.id === queda.id;

  return (
    <div className="space-y-4" data-catalogo-vivo="repetidas">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Ubicaciones repetidas</h2>
        <p className="text-sm text-gray-500 mt-0.5">Lugares con el mismo nombre dentro del mismo sitio, para dejar solo uno.</p>
      </div>
      <Ayuda id="catalogo_vivo.repetidas.pantalla" />
      {hecho && (
        <p className="text-sm text-green-800 inline-flex items-center gap-2" role="status">
          <CheckCircle2 className="h-4 w-4" aria-hidden="true" />{hecho}
        </p>
      )}
      {error && (
        <div role="alert" className="text-sm text-red-700 space-y-2">
          <p>{error}</p>
          <button type="button" onClick={() => { setError(null); cargar(); }}
            className="min-h-11 inline-flex items-center gap-2 bg-white border border-gray-300 text-gray-800 font-semibold rounded-xl px-3">
            <RefreshCw className="h-4 w-4" aria-hidden="true" /> Volver a intentarlo
          </button>
        </div>
      )}
      {grupos === null && !error && (
        <p className="text-sm text-gray-600 inline-flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Buscando repetidas…
        </p>
      )}
      {grupos?.length === 0 && (
        <p className="text-sm text-gray-600 rounded-xl border border-gray-200 p-3" data-catalogo-vivo="sin-repetidas">
          No hay ubicaciones repetidas. Cuando alguien cree un lugar con el mismo nombre que otro del mismo sitio, aparecerá aquí.
        </p>
      )}
      {grupos?.map((g) => (
        <section key={clave(g)} className="rounded-xl border border-gray-200 p-3 space-y-2" data-grupo={clave(g)}>
          <h3 className="font-semibold text-gray-900 break-words">«{g.nombre}»</h3>
          <p className="text-xs text-gray-600 break-words">Dentro de {g.ruta_padre || 'la raíz'} · {g.ubicaciones.length} con ese nombre</p>
          <fieldset className="space-y-1">
            <legend className="text-sm font-semibold text-gray-700">¿Cuál se queda?</legend>
            {g.ubicaciones.map((u) => (
              <label key={u.id} className="flex items-start gap-3 min-h-11 rounded-lg p-2 border border-gray-100">
                <input type="radio" className="mt-1 h-5 w-5" name={`queda-${clave(g)}`}
                  checked={seQueda(g) === u.id}
                  onChange={() => setElegidas((x) => ({ ...x, [clave(g)]: u.id }))} />
                <span className="text-sm">
                  <span className="block font-semibold text-gray-900">{u.nombre} <span className="font-mono text-xs text-gray-500">{u.codigo}</span></span>
                  <span className="block text-xs text-gray-600">
                    {u.productos > 0 ? `${u.productos} producto(s), ${u.unidades} unidades` : 'Vacía'}
                    {u.hijos > 0 ? ` · ${u.hijos} lugares dentro` : ''}
                  </span>
                </span>
              </label>
            ))}
          </fieldset>
          <button type="button" onClick={() => { setHecho(null); setConfirmar(g); }}
            className="w-full min-h-12 inline-flex items-center justify-center gap-2 bg-[var(--color-marca)] text-white font-semibold rounded-xl px-4">
            <Combine className="h-5 w-5" aria-hidden="true" /> Juntar en una
          </button>
          <Ayuda id="catalogo_vivo.repetidas.juntar" />
        </section>
      ))}
      <section className="rounded-xl border border-gray-200 p-3 space-y-3" data-catalogo-vivo="a-mano">
        <h3 className="font-semibold text-gray-900">Juntar dos lugares a mano</h3>
        <Ayuda id="catalogo_vivo.repetidas.a_mano" />
        <SelectorUbicacion id="cv-se-va" etiqueta="El lugar que se va" valor={seVa} onElegir={setSeVa} />
        <SelectorUbicacion id="cv-queda" etiqueta="El lugar donde queda todo" valor={queda} onElegir={setQueda} />
        <button type="button" disabled={!seVa || !queda || mismo}
          onClick={() => { setHecho(null); setConfirmarAMano(true); }}
          className="w-full min-h-12 inline-flex items-center justify-center gap-2 bg-white border border-gray-300 disabled:opacity-60 text-gray-800 font-semibold rounded-xl px-4">
          <Combine className="h-5 w-5" aria-hidden="true" /> Juntar estos dos
        </button>
        {(!seVa || !queda) && <p className="text-xs text-gray-600">Elige los dos lugares para juntarlos.</p>}
        {mismo && <p className="text-xs text-gray-600">Es el mismo lugar: elige otro en uno de los dos.</p>}
      </section>
      {confirmarAMano && (
        <ModalConfirmar
          titulo={`Juntar «${seVa.nombre}» en «${queda.nombre}»`}
          descripcion={`Lo que guarda ${seVa.codigo} pasa a ${queda.codigo} con un traspaso, y ${seVa.codigo} queda dada de baja. Esto no se deshace: su código no se vuelve a usar.`}
          pedirMotivo
          etiquetaConfirmar="Sí, juntar"
          peligro
          onConfirmar={(motivo) => juntarAMano(motivo.length >= 4 ? motivo : `Repetida: ${motivo}`)}
          onCerrar={() => setConfirmarAMano(false)}
        />
      )}
      {confirmar && (
        <ModalConfirmar
          titulo={`Juntar «${confirmar.nombre}»`}
          descripcion={`Lo que guardan las otras pasa a ${confirmar.ubicaciones.find((u) => u.id === seQueda(confirmar))?.codigo} con un traspaso, y ellas quedan dadas de baja. Esto no se deshace: sus códigos no se vuelven a usar.`}
          pedirMotivo
          etiquetaConfirmar="Sí, juntar"
          peligro
          onConfirmar={(motivo) => juntar(confirmar, motivo.length >= 4 ? motivo : `Repetida: ${motivo}`)}
          onCerrar={() => setConfirmar(null)}
        />
      )}
    </div>
  );
}
